import CryptoJS from "crypto-js";
import {
	FILE_FOR_FIELD,
	FILE_COMMIT,
	REPORT_FILE_HANDLES
} from "routes.js";
import { find } from "lodash";

class FileUploadService {
	constructor(api, $http) {
		this.api = api;
		this.$http = $http;
	}

	async processFileUpload(file, formId, fieldId) {
		const { createResponse, md5 } = await this.requestUploadHandle(file, formId, fieldId);
		if(!createResponse?.File) {
			throw new Error("server did not return a file handle");
		}

		if(!createResponse?.Uri) {
			throw new Error("server did not return an upload URI");
		}

		await this.uploadToBlob(createResponse.Uri, file, md5);
		const committedFile = await this.commitUpload(createResponse.File.Id);


		return {
			id: committedFile.Id,
			name: committedFile.Name ?? file.name,
			length: committedFile.Length ?? file.size,
			mimeType: committedFile.MimeType ?? file.type
		};
	}

	normalizeServerFile(file) {
		if(!file) {
			return undefined;
		}

		if(file.id !== undefined) {
			return file;
		}

		return {
			id: file.Id,
			name: file.Name,
			length: file.Length,
			mimeType: file.MimeType
		};
	}

	areHandlesEqual(a, b) {
		if(a === b) {
			return true;
		}

		if(!a || !b) {
			return false;
		}

		if(a.id !== undefined && b.id !== undefined) {
			return a.id === b.id;
		}

		return a.name === b.name
			&& a.length === b.length
			&& a.mimeType === b.mimeType
			&& a.$uri === b.$uri;
	}

	async fetchFileHandleUri(formId, reportId, fileId) {
		const response = await this.api.get(REPORT_FILE_HANDLES(formId, reportId, fileId));
		const handles = response?.Data || response;
		const fileHandle = Array.isArray(handles)
			? find(handles, h => h.File?.Id === fileId)
			: handles;

		return fileHandle?.Uri;
	}

	async fetchFileData(formId, reportId, handle) {
		let uri = handle.$uri;

		if(!uri && handle.id) {
			uri = await this.fetchFileHandleUri(formId, reportId, handle.id);
		}

		if(!uri) {
			return;
		}

		const response = await this.$http.get(uri, { responseType: "arraybuffer" });
		return response.data;
	}

	async requestUploadHandle(file, formId, fieldId) {
		const md5Base64 = await this.getFileMd5Base64(file);
		const createPayload = {
			Name: file.name,
			Length: file.size,
			Md5: md5Base64,
			MimeType: file.type
		};

		const createResponse = await this.api.post(
			FILE_FOR_FIELD(formId, fieldId),
			createPayload
		);

		return { createResponse, md5: md5Base64 };
	}

	uploadToBlob(uri, file, md5Base64) {
		const headers = {
			"x-ms-blob-type": "BlockBlob",
			"Content-Type": file.type || "application/octet-stream"
		};

		if(md5Base64) {
			headers["Content-MD5"] = md5Base64;
		}

		return this.$http.put(uri, file, {
			headers
		});
	}

	async commitUpload(fileId) {
		const committedFile = await this.api.post(FILE_COMMIT(fileId));
		if(!committedFile?.Id) {
			throw new Error("file commit did not return a valid file");
		}

		return committedFile;
	}

	async getFileMd5Base64(file) {
		const buf = await this.readFileAsArrayBuffer(file);
		const wordArray = CryptoJS.lib.WordArray.create(new Uint8Array(buf));
		const md5 = CryptoJS.MD5(wordArray);

		return CryptoJS.enc.Base64.stringify(md5);
	}

	readFileAsArrayBuffer(file) {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => resolve(reader.result);
			reader.onerror = () => reject(reader.error);
			reader.readAsArrayBuffer(file);
		});
	}
}

export default angular
	.module("vesl.services.file-upload-service", [])
	.service("fileUploadService", FileUploadService).name;
